import { LoaderCircleV2, SkeletonLoader, UI_CAPTION, UI_BODY_SEMIBOLD, UI_BODY_SM, colors2, MetaBlockV2, ProfileImageInput, TagDisplayV2, OKELink } from "oolib";
import React from "react";
import { useInfiniteQuery } from "react-query";
import axios from "axios";
import styled from "styled-components";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import { useElemInView } from "@/utils/customHooks/useElemInView";

const getContentTypeDisplay = (contentType, tpls) => {
  if (!contentType) return null;
  if (!tpls) return contentType;
  const tpl = tpls.find(t => t.kp_content_type === contentType);
  return tpl?.general?.content?.title || contentType;
};

const REPORT_ACTIVITY_TYPES = ['CREATE_REPORT', 'UPDATE_REPORT', 'DELETE_REPORT', 'PUBLISH_REPORT', 'RE_PUBLISH_REPORT'];
const DELETED_ACTIVITY_TYPES = ['DELETE', 'DELETE_UPDATE', 'DELETE_REPORT', 'DELETE_ANNOTATION_TAG', 'DELETE_DATASET'];
const LISTING_LINK_ACTIVITY_TYPES = ['BATCH_CONTENT_IMPORT'];

const getActivityLink = (activity) => {
  const { activityType, content } = activity;
  const contentId = content?.id;
  const contentType = content?.contentType;
  if (LISTING_LINK_ACTIVITY_TYPES.includes(activityType) && contentType) {
    return `/datasets/${contentType}`;
  }
  if (!contentId) return null;
  if (DELETED_ACTIVITY_TYPES.includes(activityType)) return null;
  if (REPORT_ACTIVITY_TYPES.includes(activityType)) {
    return `/analysis/reports/${contentId}/view`;
  }
  if (contentType && contentId) {
    return `/datasets/${contentType}/${contentId}`;
  }
  return null;
};

const DocRef = ({ title, link }) => {
  if (!title) return null;
  if (link) return <UI_BODY_SM style={{ display: "inline" }}><OKELink to={link} text={title} /></UI_BODY_SM>;
  return <UI_BODY_SM style={{ display: "inline", fontStyle: "italic" }}>{title}</UI_BODY_SM>;
};

const buildActivityMessage = ({ activityType, docTitle, link, metadata }) => {
  const ref = docTitle
    ? <DocRef title={docTitle} link={link} />
    : null;

  const titled = (verb) => ref
    ? <>{verb} the document titled {ref}</>
    : <>{verb} a document</>;

  switch (activityType) {
    case 'CREATE':       return titled('created');
    case 'UPDATE':       return titled('updated');
    case 'DELETE':       return titled('deleted');
    case 'PUBLISH':      return titled('published');
    case 'RE_PUBLISH':   return titled('edited');
    case 'SUBMIT':       return titled('submitted');
    case 'RE_SUBMIT':    return titled('re-submitted');
    case 'APPROVE':      return titled('approved');
    case 'MODERATE_APPROVE': return titled('approved (via moderation)');
    case 'SENT_BACK':    return titled('sent back');

    case 'CREATE_UPDATE': return titled('created a draft of');
    case 'UPDATE_UPDATE': return titled('updated the draft of');
    case 'DELETE_UPDATE': return titled('deleted the draft of');

    case 'TAKEN_UNDER_MODERATION':
      return titled('took under moderation');
    case 'MOVED_TO_AWAITING_REVIEW':
      return titled('moved to awaiting review');

    case 'ADD_COMMENT':
      return ref ? <>commented on {ref}</> : <>commented on a document</>;
    case 'ADD_REACTION':
    case 'ADD_REACTION_LIKE':
      return ref ? <>reacted to {ref}</> : <>reacted to a document</>;
    case 'MENTION_IN_COMMENT':
      return ref ? <>was mentioned in a comment on {ref}</> : <>was mentioned in a comment</>;

    case 'BATCH_CONTENT_IMPORT': {
      const count = metadata?.successCount;
      const docsLabel = count
        ? <>{count} document{count !== 1 ? 's' : ''}</>
        : <>documents</>;
      return link
        ? <>batch imported {docsLabel} — <OKELink to={link} text="View dataset" /></>
        : <>batch imported {docsLabel}</>;
    }

    case 'CREATE_ANNOTATION':
      return ref ? <>added an annotation to {ref}</> : <>added an annotation</>;
    case 'UPDATE_ANNOTATION':
      return ref ? <>updated an annotation on {ref}</> : <>updated an annotation</>;
    case 'DELETE_ANNOTATION':
      return ref ? <>removed an annotation from {ref}</> : <>removed an annotation</>;

    case 'AI_SUGGEST_ANNOTATION':
      return ref ? <>used AI to suggest annotations on {ref}</> : <>used AI to suggest annotations</>;
    case 'AUTO_ANNOTATE':
      return ref ? <>auto-annotated {ref}</> : <>auto-annotated a document</>;

    case 'CREATE_REPORT':
    case 'PUBLISH_REPORT':
      return ref ? <>published the report {ref}</> : <>published a report</>;
    case 'UPDATE_REPORT':
    case 'RE_PUBLISH_REPORT':
      return ref ? <>edited the report {ref}</> : <>edited a report</>;
    case 'DELETE_REPORT':
      return ref ? <>deleted the report titled {ref}</> : <>deleted a report</>;

    case 'CREATE_ANNOTATION_TAG':
      return ref ? <>created the tag {ref}</> : <>created a tag</>;
    case 'BULK_CREATE_ANNOTATION_TAG':
      return <>created {metadata?.count || ''} tags</>;
    case 'UPDATE_ANNOTATION_TAG':
      return ref ? <>updated the tag {ref}</> : <>updated a tag</>;
    case 'DELETE_ANNOTATION_TAG':
      return ref ? <>deleted the tag titled {ref}</> : <>deleted a tag</>;

    case 'DELETE_DATASET':
      return ref ? <>deleted the dataset titled {ref}</> : <>deleted a dataset</>;

    default:
      return ref ? <>{activityType} — {ref}</> : <>{activityType}</>;
  }
};

const ActivityRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${colors2.grey5};
  &:last-child { border-bottom: none; }
`;

const LIMIT = 15;

const ActivityRowSkeleton = () => (
  <ActivityRow>
    <SkeletonLoader style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0 }} />
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
      <SkeletonLoader style={{ height: 10, width: 60, borderRadius: 4 }} />
      <SkeletonLoader style={{ height: 12, width: "80%", borderRadius: 4 }} />
      <SkeletonLoader style={{ height: 10, width: 100, borderRadius: 4 }} />
    </div>
  </ActivityRow>
);

export const RecentActivity = ({ fullHeight = false }) => {
  const tpls = useGetQueryData('allTpls');

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["recentActivity"],
    queryFn: ({ pageParam: page = 1 }) =>
      axios
        .get(`/api/activityAnalytics/recentActivity?page=${page}&limit=${LIMIT}`)
        .then((res) => res.data),
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
  });

  const { observerRef } = useElemInView({
    callback: fetchNextPage,
    enabled: !!hasNextPage && !isFetchingNextPage,
  });

  if (isLoading) return <div style={{ padding: "1.5rem", textAlign: "center" }}><LoaderCircleV2 /></div>;
  if (isError) return <UI_CAPTION color={colors2.red}>Failed to load activity</UI_CAPTION>;

  const activities = data.pages.flatMap((p) => p.data);
  if (!activities.length) return <UI_CAPTION color={colors2.grey50}>No recent activity yet</UI_CAPTION>;

  return (
    // Widget mode caps height with internal scroll; full-page mode (Activity tab)
    // drops the cap and lets the page scroll.
    <div style={fullHeight ? { padding: "0 12px" } : { maxHeight: 500, overflowY: "auto", padding: "0 12px" }}>
      {activities.map((a, i) => {
        const userName = a.user?.name || "System";
        const contentType = a.content?.contentType;
        const contentTypeDisplay = getContentTypeDisplay(contentType, tpls);
        const docTitle = a.content?.title && a.content.title !== "untitled" ? a.content.title : null;
        const link = getActivityLink(a);

        const message = buildActivityMessage({
          activityType: a.activityType,
          docTitle,
          link,
          metadata: a.metadata,
        });

        return (
          <ActivityRow key={a._id || i}>
            <div style={{ paddingTop: 2 }}>
              <ProfileImageInput
                readOnly
                value={Array.isArray(a.user?.avatar?.userUploaded) ? a.user.avatar.userUploaded : undefined}
                size={30}
                imageTitle={userName}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {contentTypeDisplay && (
                <div style={{ marginBottom: 3 }}>
                  <TagDisplayV2
                    display={contentTypeDisplay.toUpperCase()}
                    XS
                  />
                </div>
              )}
              <UI_BODY_SM>
                <UI_BODY_SEMIBOLD style={{ fontSize: 13, display: "inline" }}>{userName}</UI_BODY_SEMIBOLD>
                {" "}{message}
              </UI_BODY_SM>
              <div style={{ marginTop: 4 }}>
                <MetaBlockV2
                  data={a}
                  config={[{ key: "date", value: a.dateCreated }]}
                />
              </div>
            </div>
          </ActivityRow>
        );
      })}
      {hasNextPage && (
        <div ref={observerRef}>
          {isFetchingNextPage && Array.from({ length: LIMIT }, (_, i) => (
            <ActivityRowSkeleton key={`skel-${i}`} />
          ))}
        </div>
      )}
    </div>
  );
};
